/* Today's Invoices extras: no-weight badge (opens the sheet on screen), admin member picker,
 * weekly Corrections Report. */
import { PwInput } from "@/components/ui/pw-input";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, Scale, Unlock, Wrench } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const DOCS = [["packing_list", "Packing List"], ["bulk_data", "Bulk Data"], ["bulk_label", "Bulk Label"]];

/* Emirates tracking progress: "Emirates tracking 12/20" until every parcel has a real number, then green "complete". */
export const EmiTrackBadge = ({ inv }) => {
  const w = inv.weight || {};
  if (!w.emi_total) return null;
  const real = w.emi_total - (w.emi_temp || 0);
  const done = !w.emi_temp;
  return (
    <div className={`mt-1 inline-block whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${done ? "bg-emerald-100 text-emerald-700" : "bg-rose-50 text-rose-700 ring-1 ring-rose-200"}`}
      title={done ? "Every Emirates parcel has a real tracking number" : `${w.emi_temp} Emirates parcel(s) still on a temporary EMI… number — double-click TRACK NO. in the Packing List`}
      data-testid={`or-today-inv-emi-${inv.invoice_key}`}>
      {done ? "Emirates tracking complete" : `Emirates tracking ${real}/${w.emi_total}`}
    </div>
  );
};

export const NoWeightBadge = ({ inv }) => {
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState(null);
  const w = inv.weight || {};
  if (!w.missing) return <span className="text-slate-300">—</span>;
  const toggle = async (e) => {
    e.stopPropagation();
    setOpen(!open);
    if (!docs) {
      try { setDocs((await axios.get(`${API}/order-register/invoice-docs/${encodeURIComponent(inv.invoice_no)}`, { headers: getAuthHeader() })).data); }
      catch { setDocs({}); }
    }
  };
  const go = (e, k) => {
    e.stopPropagation();
    const d = docs?.[k];
    if (!d) { toast.error(`No ${DOCS.find(x => x[0] === k)[1]} made for ${inv.invoice_no} yet`); return; }
    const url = k === "bulk_label"
      ? `${API}/bulk-label/history/${d.id}/preview?token=${encodeURIComponent(localStorage.getItem("token") || "")}`
      : `/preview?module=${k}&id=${d.id}&nowt=1`;
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
  };
  return (
    <span className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={toggle} title={`${w.missing} of ${w.total} parcels have no weight — click to open a sheet`}
        className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold text-white ${w.missing === w.total ? "bg-rose-600" : "bg-amber-500"}`}
        data-testid={`or-today-inv-nowt-${inv.invoice_key}`}><Scale className="h-3 w-3" />{w.missing}/{w.total}</button>
      {open && (
        <span className="absolute right-0 top-full z-30 mt-1 flex w-40 flex-col rounded-lg border bg-white p-1 text-left text-[11px] shadow-lg" data-testid={`or-today-inv-nowt-menu-${inv.invoice_key}`}>
          <span className="px-2 py-1 text-[10px] font-semibold text-slate-500">Open on screen:</span>
          {DOCS.map(([k, l]) => (
            <button key={k} type="button" onClick={(e) => go(e, k)} disabled={!docs}
              className="flex items-center justify-between rounded px-2 py-1.5 hover:bg-slate-100 disabled:opacity-50" data-testid={`or-today-inv-nowt-open-${k}-${inv.invoice_key}`}>
              {l}{!docs ? <Loader2 className="h-3 w-3 animate-spin" /> : !docs[k] && <span className="text-[9px] text-slate-400">not made</span>}</button>))}
        </span>)}
    </span>
  );
};

export const MemberPicker = ({ data, value, onChange }) => (data?.members || []).length > 0 || value ? (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="h-8 rounded border bg-white px-2 text-xs" data-testid="or-today-inv-member">
    <option value="">All team members</option>
    {(data?.members || []).map((m) => <option key={m.email} value={m.email}>{m.name}</option>)}
  </select>) : null;

const monday = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const CorrectionsReport = ({ member }) => {
  const [open, setOpen] = useState(false);
  const [wk, setWk] = useState(() => iso(monday(new Date())));
  const [d, setD] = useState(null);
  const load = useCallback(() => axios.get(`${API}/order-register/corrections-report`, { headers: getAuthHeader(), params: { week_start: wk, member: member || undefined } })
    .then((r) => setD(r.data)).catch(() => setD(null)), [wk, member]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const shift = (n) => { const x = new Date(wk); x.setDate(x.getDate() + 7 * n); setWk(iso(x)); };
  return (
    <div className="rounded border" data-testid="or-corrections-report">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 bg-amber-50 px-2 py-1.5 text-left text-[11px] font-bold text-amber-800" data-testid="or-corrections-toggle">
        <Wrench className="h-3.5 w-3.5" /> Corrections report (invoices changed after completion)
        {d && <span className="rounded-full bg-amber-500 px-1.5 text-white" data-testid="or-corrections-total">{d.total}</span>}
        <span className="ml-auto">{open ? "Hide" : "Show"}</span></button>
      {open && (
        <div className="space-y-2 p-2 text-[11px]">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => shift(-1)} className="rounded border p-0.5" data-testid="or-corrections-prev"><ChevronLeft className="h-3.5 w-3.5" /></button>
            <b data-testid="or-corrections-week">{d ? `${d.week_start} → ${d.week_end}` : wk}</b>
            <button type="button" onClick={() => shift(1)} className="rounded border p-0.5" data-testid="or-corrections-next"><ChevronRight className="h-3.5 w-3.5" /></button>
          </div>
          {!d ? <Loader2 className="h-4 w-4 animate-spin" /> : !d.people.length ? <p className="text-slate-400" data-testid="or-corrections-empty">No corrected invoices this week</p> :
            d.people.map((p) => (
              <div key={p.email} className="rounded border bg-white" data-testid={`or-corrections-person-${p.email}`}>
                <div className="flex items-center gap-2 border-b bg-slate-50 px-2 py-1 font-semibold">{p.name}
                  <span className="rounded-full bg-amber-500 px-1.5 text-white">{p.count} corrected</span></div>
                {p.items.map((it, i) => (
                  <div key={i} className="border-b px-2 py-1 last:border-0">
                    <span className="font-mono font-bold">{it.invoice_no}</span>
                    <span className="ml-2 text-slate-400">{new Date(it.at).toLocaleString()}</span>
                    <ul className="ml-4 list-disc text-slate-700">{it.changes.map((c, j) => <li key={j}>{c}</li>)}</ul>
                  </div>))}
              </div>))}
        </div>)}
    </div>
  );
};

export const ReleaseInvoiceButton = ({ inv, onDone }) => {
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  if (inv.status === "completed" || !inv.owner_name) return null;
  const go = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/order-register/invoice/${encodeURIComponent(inv.invoice_no)}/release-verified`, { password: pwd }, { headers: getAuthHeader() });
      toast.success(r.data?.unchanged ? `${inv.invoice_no} was already free` : `${inv.invoice_no} released — anyone can use it now`);
      setOpen(false); setPwd(""); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Release failed"); }
    setBusy(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
      <button type="button" onClick={(e) => e.stopPropagation()} title={`Release ${inv.invoice_no}${inv.owner_name ? ` (held by ${inv.owner_name})` : ""}`}
        className="inline-flex items-center gap-0.5 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 hover:bg-rose-50 hover:text-rose-700"
        data-testid={`ti-release-btn-${inv.invoice_key}`}><Unlock className="h-3 w-3" />Release</button>
      </PopoverTrigger>
        <PopoverContent align="end" className="flex w-60 flex-col gap-1.5 p-2 text-left text-[11px]" onClick={(e) => e.stopPropagation()} data-testid={`ti-release-pop-${inv.invoice_key}`}>
          <span className="text-slate-600">Free <b>{inv.invoice_no}</b>{inv.owner_name ? <> from <b>{inv.owner_name}</b></> : ""}? Enter supervisor password.</span>
          <PwInput autoFocus value={pwd} onChange={(e) => setPwd(e.target.value)} onKeyDown={(e) => e.key === "Enter" && pwd && go()}
            className="h-7 rounded border px-2 text-xs" placeholder="Supervisor password" data-testid={`ti-release-pwd-${inv.invoice_key}`} />
          <button type="button" disabled={!pwd || busy} onClick={go}
            className="h-7 rounded bg-rose-600 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50" data-testid={`ti-release-confirm-${inv.invoice_key}`}>
            {busy ? "Releasing…" : "Release invoice"}</button>
        </PopoverContent>
    </Popover>
  );
};

const fmtAt = (v) => (v ? new Date(v).toLocaleString([], { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

export const ReleasedTag = ({ inv }) => !inv.released_at ? null : (
  <span className="ml-1 rounded bg-rose-50 px-1 text-[9px] font-sans font-semibold text-rose-700 ring-1 ring-rose-200"
    title={`Released by ${inv.released_by} at ${new Date(inv.released_at).toLocaleString()}`}
    data-testid={`ti-released-tag-${inv.invoice_key}`}>Released by {String(inv.released_by || "").split("@")[0]}, {fmtAt(inv.released_at)}</span>
);

export const ReleaseHistory = ({ data }) => (data?.releases || []).length === 0 ? null : (
  <div className="rounded border border-rose-200 bg-white" data-testid="ti-release-history">
    <div className="border-b border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-bold text-rose-700">Invoice releases in these dates ({data.releases.length})</div>
    <div className="max-h-40 overflow-y-auto text-[11px]">
      {data.releases.map((r, i) => (
        <div key={i} className="flex gap-3 border-t border-rose-50 px-3 py-1" data-testid={`ti-release-history-row-${i}`}>
          <b className="font-mono">{r.invoice_no}</b>
          <span className="text-slate-600">freed from <b>{r.from || "—"}</b> by <b>{r.by}</b></span>
          <span className="ml-auto text-slate-400">{fmtAt(r.at)}</span>
        </div>))}
    </div>
  </div>
);
